import React from 'react';

import { LoadableElement } from '@99designs/common/components/LoadableElement';
import { FormattedMessage } from '@99designs/common/i18n';
import {
    getSquarespaceBrief,
    getSquarespaceBriefVariables,
} from '__generated__/getSquarespaceBrief';
import { SquarespaceBriefFields } from '__generated__/SquarespaceBriefFields';
import {
    updateSquarespaceBrief,
    updateSquarespaceBriefVariables,
} from '__generated__/updateSquarespaceBrief';
import { handleGraphQLError } from 'common/utils/Apollo';
import { AuthConsumer } from 'projects/components/AuthContext';
import { errorNotFound, errorServerError } from 'projects/components/ErrorBoundary';
import { routeForBrief } from 'projects/routes';
import Brief from 'projects/routes/Brief';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import { createStepState, StepConfiguration } from 'projects/util/step';
import { ApolloConsumer, Query } from 'react-apollo';
import { Redirect, RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import { getIndustry } from './industry';
import queries from './SquarespaceBrief.graphql';
import SummaryPage from './SummaryPage';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

const SquarespaceBrief: React.FunctionComponent<Props> = props => (
    <Query<getSquarespaceBrief, getSquarespaceBriefVariables>
        query={queries.getSquarespaceBrief}
        variables={{ id: props.match.params.briefId }}
    >
        {({ error, loading, data }) => {
            if (loading) {
                return <LoadableElement variations={['fade-in']} isLoading={true} />;
            }

            if (error) {
                handleGraphQLError(error);
            }

            if (!data) {
                throw errorServerError;
            }

            if (!data.brief) {
                throw errorNotFound;
            }

            if (data.brief.fields.__typename !== 'SquarespaceBriefFields') {
                return (
                    <Redirect
                        to={routeForBrief(
                            data.brief.fields.__typename,
                            data.brief.id,
                            props.location.search
                        )}
                    />
                );
            }

            const brief = {
                ...data.brief,
                fields: data.brief.fields as SquarespaceBriefFields,
            };

            const steps: StepConfiguration[] = [
                {
                    step: 1,
                    title: __('Tell us about your project'),
                },
                { step: 2, title: __('About you') },
                { step: 3, title: __('Review your brief') },
                {
                    step: 4,
                    title: __('Invite designers to quote'),
                    requiresAuth: true,
                    isDesignerStep: true,
                    nextActionLabel: __('Confirm designers'),
                },
            ];

            const description = () => {
                let selection = [brief.category && brief.category.title];

                const industry = getIndustry((brief.fields as SquarespaceBriefFields).industry);
                if (industry) {
                    selection = selection.concat(industry);
                }

                return (
                    <FormattedMessage
                        message={__(
                            "Based on your selection {selection} we've matched you with some awesome designers"
                        )}
                        values={{
                            selection: <strong>{selection.join(' / ')}</strong>,
                        }}
                    />
                );
            };

            return (
                <ApolloConsumer>
                    {client => (
                        <AuthConsumer>
                            {({ login }) => (
                                <Brief
                                    brief={brief}
                                    viewer={data.viewer}
                                    login={login}
                                    step={createStepState({
                                        steps,
                                        brief: data.brief,
                                        currentStep: parseInt(props.match.params.step, 10),
                                        designerSources: data.brief.designersWithSource.map(
                                            d => d.source
                                        ),
                                    })}
                                    client={client}
                                    onSave={async changes => {
                                        const res = await client.mutate<
                                            updateSquarespaceBrief,
                                            updateSquarespaceBriefVariables
                                        >({
                                            mutation: queries.updateSquarespaceBrief,
                                            variables: {
                                                id: brief.id,
                                                fields: { squarespace: changes },
                                            },
                                        });
                                        return res ? res.data!.brief.errors : [];
                                    }}
                                >
                                    {form => (
                                        <Switch>
                                            <Route
                                                path={`/briefs/squarespace/:briefId/1`}
                                                render={() => (
                                                    <DetailsPage brief={brief} form={form} />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/2`}
                                                render={() => (
                                                    <AboutYouPage form={form} brief={brief} />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/3`}
                                                render={() => <SummaryPage brief={brief} />}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/4`}
                                                render={() => (
                                                    <InviteDesignersPage
                                                        brief={brief}
                                                        viewer={data.viewer}
                                                    >
                                                        {description()}
                                                    </InviteDesignersPage>
                                                )}
                                            />
                                        </Switch>
                                    )}
                                </Brief>
                            )}
                        </AuthConsumer>
                    )}
                </ApolloConsumer>
            );
        }}
    </Query>
);

export default withRouter(SquarespaceBrief);
